import {Component, computed, inject, input, Optional, SkipSelf} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  FormGroup,
  FormGroupDirective,
  FormGroupName,
  ReactiveFormsModule,
} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {SelectModule} from 'primeng/select';
import {InputTextModule} from 'primeng/inputtext';
import {TranslocoPipe} from '@jsverse/transloco';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {DirectusTranslatedItem} from '@frontend/directus';
import {DirectusTooltipComponent} from '../../../directus-tooltip/directus-tooltip.component';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {startWith, switchMap, of} from 'rxjs';

@Component({
  selector: 'app-form-field-select',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SelectModule,
    InputTextModule,
    TranslocoPipe,
    TranslateDirectusPipe,
    DirectusTooltipComponent,
  ],
  templateUrl: './form-field-select.component.html',
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: (parentGroupName: FormGroupName, parentFormDir: FormGroupDirective) =>
        parentGroupName ?? parentFormDir,
      deps: [
        [new Optional(), new SkipSelf(), FormGroupName],
        [new Optional(), new SkipSelf(), FormGroupDirective],
      ],
    },
  ],
})
export class FormFieldSelectComponent {
  id = input.required<string>();
  fieldName = input.required<string>();
  label = input<string>('');
  placeholder = input<string>('common.select');
  isReadOnly = input<boolean>(false);
  smallLabel = input<boolean>(false);
  showClear = input<boolean>(true);
  filter = input<boolean>(false);
  options = input<DirectusTranslatedItem[]>([]);

  @Optional() private readonly controlContainer = inject(ControlContainer);

  get form(): FormGroup {
    return this.controlContainer?.control as FormGroup;
  }

  readonly formControlValue = toSignal(
    toObservable(this.fieldName).pipe(
      switchMap((name) => {
        const ctrl = this.form?.get(name);
        return ctrl ? ctrl.valueChanges.pipe(startWith(ctrl.value)) : of(null);
      }),
    ),
  );

  readonly isRequired = computed(() => {
    const control = this.form?.get(this.fieldName());
    if (!control || !control.validator) return false;
    const validator = control.validator({value: ''} as AbstractControl);
    return !!validator?.['required'];
  });

  readonly selectedOptionTranslations = computed(() => {
    const value = this.formControlValue();
    const selected = this.options().find((opt) => opt.id === value);
    return selected ? selected.translations : null;
  });
}
